import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { TextField } from "@/components/TextField";
import { Typeahead } from "@/components/Typeahead";
import { AddressFieldset } from "@/components/AddressFieldset";
import { PhoneListField } from "@/components/PhoneListField";
import {
  accountWebsite,
  searchNaicsCodes,
  updateAccount,
  type AccountDetail,
  type AccountInput,
  type NaicsCode,
} from "@/lib/brokerAccounts";
import {
  EMPTY_ADDRESS,
  formatAddress,
  isCompleteAddress,
  trimAddress,
  type AddressValue,
} from "@/lib/address";
import {
  formatE164ForDisplay,
  phoneRowErrors,
  primaryPhone,
  toPhoneInputs,
  toPhoneValues,
  type PhoneValue,
} from "@/lib/phone";
import { NOT_PROVIDED, orNotProvided } from "@/lib/display";
import { lastFiscalYearRevenue } from "@/lib/accountRevenue";
import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
import { anchorServerError } from "@/lib/serverFieldErrors";
import { WELL } from "@/base";

// BRK-S138 — the account-info block on the Surety account profile.
//
// The facts Signal reads off an insured before writing a bond: who they are
// legally, what they trade as, where they are, who owns them, what they build,
// and the three ways to reach them. It is a RECORD first — the broker reads it
// far more often than they change it — so it renders as a description list and
// only becomes a form when they ask.
//
// ── EVERY FACT ON THIS BLOCK NOW EDITS HERE (BRK-S176) ──────────────────────
//
// It used to edit exactly two — the website and the General Trade — and send the
// broker to `/accounts/{id}` for the rest, on the reasoning that reimplementing
// the record here would be a second capture surface. The scoping document
// settles it the other way: the surety account page is "readable, editable", and
// a broker chasing a changed phone number on the page they are already reading
// should not be bounced onto the Commercial card to change it.
//
// What keeps that from becoming a second capture surface is that it is a PARTIAL
// PUT of exactly the fields this block RENDERS. Personnel, the mailing address,
// the entity type, the servicing broker and the construction-management system
// are not here, and a save from here cannot touch them — an omitted key means
// "leave it unchanged". The full record still edits through `AccountForm`, which
// still owns every field this one does not draw, and the footer still links to
// it.
//
// TWO FACTS DELIBERATELY DO NOT EDIT:
//
//   • **EIN** — read-only. It is the insured's federal tax id, unique per
//     brokerage upstream, and a typo in it is not a correction a broker makes in
//     passing on a bond profile. It is SHOWN, because an underwriter reads it
//     out, and shown as text rather than as a disabled input: a greyed-out box
//     invites a click that does nothing.
//   • **Principal owner** — DERIVED server-side from the personnel holding an
//     ownership share, never re-derived and never entered (PMS-S238 /
//     BRK-S110). Editing it would mean editing a person, which is the roster's
//     job.
//
// LAID OUT AS THE WIREFRAME DRAWS IT (BRK-S401): legal name, DBA, EIN, legal
// address, principal owner, last fiscal year revenue, general trade, phone,
// email, website. The revenue row is READ-ONLY here — `annualRevenues` edits on
// the full account record — and an absence never reads as $0
// (`lastFiscalYearRevenue`).
//
// EVERY EMPTY VALUE SAYS SO IN WORDS (`orNotProvided`). An em dash on a page a
// broker reads down the phone cannot be told apart from a rendering bug.

/** One label/value row. `value` is already a display string — the formatting
 *  decision belongs to the caller, which is what keeps D-10 in one place. */
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <dt className="text-caption font-medium uppercase tracking-wide text-brk-dim">
        {label}
      </dt>
      <dd className="break-words font-medium text-brk-text">{value}</dd>
    </div>
  );
}

/** How a NAICS classification reads: the code, then the trade it names. */
function naicsText(account: AccountDetail): string {
  if (account.naics) {
    return `${account.naics.code} — ${account.naics.description}`;
  }
  // A captured code the table cannot name is a REAL state (see `NaicsCode`), and
  // the bare code is the honest rendering of it — better than "not provided",
  // which would deny a classification the record holds.
  return orNotProvided(account.naicsCode);
}

/** The stored address as the editor's value — blank when there is none. */
function toAddressValue(address: AccountDetail["legalAddress"]): AddressValue {
  if (!address) return { ...EMPTY_ADDRESS };
  return {
    street: address.street,
    city: address.city,
    state: address.state,
    zip: address.zip,
  };
}

/** The field names this block renders, so a server reason can be anchored under
 *  the control it names rather than becoming a block-level line (BRK-S092). */
const ANCHORABLE = [
  "legalName",
  "dbaName",
  "emailAddress",
  "phones",
  "legalAddress",
  "website",
  "naicsCode",
] as const;

export function SuretyAccountInfo({
  detail,
  canEdit,
  onUpdated,
}: {
  detail: AccountDetail;
  canEdit: boolean;
  /** The refreshed detail after a save — the page owns the record. */
  onUpdated: (detail: AccountDetail) => void;
}) {
  const { authedRequest } = useAuth();
  const [editing, setEditing] = useState(false);
  const [legalName, setLegalName] = useState(detail.legalName);
  const [dbaName, setDbaName] = useState(detail.dbaName);
  const [emailAddress, setEmailAddress] = useState(detail.emailAddress);
  const [phones, setPhones] = useState<PhoneValue[]>(
    toPhoneValues(detail.phones),
  );
  const [legalAddress, setLegalAddress] = useState<AddressValue>(
    toAddressValue(detail.legalAddress),
  );
  const [website, setWebsite] = useState(accountWebsite(detail) ?? "");
  const [naics, setNaics] = useState<NaicsCode | null>(detail.naics);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState<Record<string, string>>({});
  // Phone rejections are shown only once the broker has tried to save, the same
  // rule `AccountForm` follows: complaining about a half-typed number is an
  // error the broker has not made yet.
  const [attempted, setAttempted] = useState(false);

  // Stable for `Typeahead`'s effect dependency — see its header.
  const search = useCallback(
    async (query: string) => {
      const page = await searchNaicsCodes(authedRequest, query);
      return { items: page.items };
    },
    [authedRequest],
  );

  function startEditing() {
    setLegalName(detail.legalName);
    setDbaName(detail.dbaName);
    setEmailAddress(detail.emailAddress);
    setPhones(toPhoneValues(detail.phones));
    setLegalAddress(toAddressValue(detail.legalAddress));
    setWebsite(accountWebsite(detail) ?? "");
    setNaics(detail.naics);
    setError(null);
    setInvalid({});
    setAttempted(false);
    setEditing(true);
  }

  const phoneErrors = attempted ? phoneRowErrors(phones) : undefined;

  async function save() {
    setAttempted(true);
    // The legal name is the ONE field the contract always requires, so it is the
    // one this block validates itself; everything else is the server's call and
    // comes back anchored.
    if (!legalName.trim()) {
      // ANCHORED and not also summarised: the anchoring rule (BRK-S092) is that
      // only what cannot be placed becomes the block-level line, and one
      // sentence printed twice on one card reads as two problems.
      setError(null);
      setInvalid({ legalName: "Enter the insured’s legal name." });
      return;
    }
    // A number the API would refuse never leaves the browser — the BRK-S073
    // rule: this portal's verdict IS the API's (`normalizeUsPhone` is a verbatim
    // mirror), so the rejection belongs on the offending row rather than after a
    // round-trip.
    if (phoneRowErrors(phones).some((e) => e !== null)) {
      setError("Check the phone numbers below.");
      return;
    }
    setBusy(true);
    setError(null);
    setInvalid({});
    try {
      // A PARTIAL PUT (BRK-S057): only the fields this block renders travel, so
      // a save here can never disturb personnel, the mailing address or a
      // servicing broker it does not draw.
      //
      // A BLANK FIELD IS OMITTED, NOT SENT AS "". That is the contract's rule
      // and not a choice here — PMS 400s an empty string, and an omitted key
      // means "leave it unchanged". The consequence is real and shared with the
      // account form: this block can SET and CHANGE a value, and cannot CLEAR
      // one. Sending "" to work around it would trade a missing affordance for a
      // rejected save.
      const phoneInputs = toPhoneInputs(phones);
      const input: AccountInput = {
        legalName: legalName.trim(),
        ...(dbaName.trim() ? { dbaName: dbaName.trim() } : {}),
        ...(emailAddress.trim() ? { emailAddress: emailAddress.trim() } : {}),
        // Omitted rather than `[]` when the broker has cleared every row: an
        // empty array CLEARS the insured's numbers upstream (PMS-S173), and
        // "I left this alone" must never be spelled the same way as "delete
        // them all".
        ...(phoneInputs.length ? { phones: phoneInputs } : {}),
        // The contract has no half-address, so a partial one is omitted whole
        // rather than sent in pieces.
        ...(isCompleteAddress(legalAddress)
          ? { legalAddress: trimAddress(legalAddress) }
          : {}),
        ...(website.trim() ? { website: website.trim() } : {}),
        ...(naics ? { naicsCode: naics.code } : {}),
      };
      const fresh = await updateAccount(authedRequest, detail.id, input);
      onUpdated(fresh);
      setEditing(false);
    } catch (err) {
      // The same anchoring the bond flow uses (BRK-S092): a reason the server
      // names for a field lands UNDER that field, and only what cannot be
      // placed becomes the block-level line.
      const anchored = anchorServerError(err, ANCHORABLE);
      setInvalid(anchored.byField);
      setError(anchored.summary);
    } finally {
      setBusy(false);
    }
  }

  const primary = primaryPhone(detail.phones) ?? null;
  // Only a program IN FORCE names a type (`programType` is null otherwise).
  const revenue = lastFiscalYearRevenue(
    detail.annualRevenues,
    detail.preapproval?.status === "approved"
      ? detail.preapproval.programType
      : null,
  );

  return (
    <section
      aria-labelledby="surety-account-info"
      className={`flex flex-col gap-5 ${WELL}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <h2 id="surety-account-info" className="text-heading text-brk-text">
          Account information
        </h2>
        {canEdit && !editing && (
          <button
            type="button"
            onClick={startEditing}
            className={buttonClass({ variant: "secondary", size: "sm" })}
          >
            Edit
          </button>
        )}
      </div>

      {/* The RECORD. In edit mode it keeps only the two facts that do not edit —
          EIN and the derived principal owner — so the page never shows a value
          and an input for the same thing at once.

          Single column at 375, two from `sm:` up — the mobile-first default,
          never the reverse. */}
      <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {!editing && (
          <>
            <Fact label="Legal name" value={orNotProvided(detail.legalName)} />
            <Fact label="DBA" value={orNotProvided(detail.dbaName)} />
          </>
        )}
        {/* READ-ONLY, in both modes — see the header. */}
        <Fact label="EIN" value={orNotProvided(detail.ein)} />
        {!editing && (
          <Fact
            label="Business legal address"
            value={
              detail.legalAddress
                ? formatAddress(detail.legalAddress)
                : NOT_PROVIDED
            }
          />
        )}
        <Fact
          label="Principal owner"
          value={
            detail.principalOwner
              ? `${detail.principalOwner.name} (${detail.principalOwner.ownershipPercentage}%)`
              : NOT_PROVIDED
          }
        />
        <Fact label="Last fiscal year revenue" value={revenue} />
        {!editing && (
          <>
            <Fact label="General trade" value={naicsText(detail)} />
            <Fact
              label="Phone"
              value={
                primary
                  ? formatE164ForDisplay(primary.e164, primary.extension)
                  : NOT_PROVIDED
              }
            />
            <Fact label="Email" value={orNotProvided(detail.emailAddress)} />
            <Fact
              label="Website"
              value={
                accountWebsite(detail) ? (
                  // `noreferrer` alongside `noopener`: the insured's own site
                  // has no business learning which portal page linked to it.
                  <a
                    href={accountWebsite(detail) ?? ""}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brk-accent hover:underline"
                  >
                    {accountWebsite(detail)}
                  </a>
                ) : (
                  NOT_PROVIDED
                )
              }
            />
          </>
        )}
      </dl>

      {editing && (
        <div className="flex flex-col gap-4 border-t border-brk-border pt-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <TextField
              label="Legal name"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              required
              error={invalid.legalName}
            />
            <TextField
              label="DBA"
              value={dbaName}
              onChange={(e) => setDbaName(e.target.value)}
              optional
              error={invalid.dbaName}
            />
            <TextField
              label="Email"
              // D-10 rule 1: the control matches the type.
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              optional
              error={invalid.emailAddress}
            />
            <Typeahead
              label="General trade (NAICS)"
              placeholder="Search by code or trade"
              search={search}
              selected={naics}
              onSelect={setNaics}
              keyOf={(c) => c.code}
              labelOf={(c) => `${c.code} — ${c.description}`}
              renderOption={(c) => (
                <>
                  <span className="tabular-nums font-medium text-brk-text">
                    {c.code}
                  </span>
                  <span className="text-xs text-brk-dim">{c.description}</span>
                </>
              )}
              optional
              error={invalid.naicsCode}
            />
            <TextField
              label="Website"
              // D-10 rule 1 again: a URL field gets the browser's own
              // affordances and, on a phone, the right keyboard.
              type="url"
              placeholder="https://"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              optional
              error={invalid.website}
            />
            <div className="sm:col-span-2">
              {/* The same repeatable editor the account form uses — one number
                  or several, exactly one marked primary (PMS-S173). It labels
                  its own rows, so there is no heading above it to say "Phone"
                  twice. */}
              <PhoneListField
                label="Phone"
                value={phones}
                onChange={setPhones}
                errors={phoneErrors}
              />
            </div>
            <div className="sm:col-span-2">
              <AddressFieldset
                title="Business legal address"
                value={legalAddress}
                onChange={setLegalAddress}
                bare
                groupLabel="Business legal address"
                error={invalid.legalAddress}
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="text-sm text-brk-danger">
              {error}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className={buttonClass()}
            >
              {busy ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className={`text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {canEdit && !editing && (
        // The REST of the record — personnel, the mailing address, the entity
        // type, the servicing broker — still edits where it already does. One
        // capture surface per fact, rather than a second form that has to keep
        // up.
        <Link
          to={`/accounts/${detail.id}`}
          className={`self-start text-sm text-brk-accent hover:underline ${TOUCH_TARGET}`}
        >
          Edit the full account record →
        </Link>
      )}
    </section>
  );
}
